<template>
  <div class="container-fluid p-4">
    <div class="box mt-3">
      <div class="page-header">
        <h2>{{ $t('eventOrganizers.title') }} > {{ isEdit ? $t('eventOrganizers.edit') : $t('eventOrganizers.create') }}</h2>
      </div>

      <form @submit.prevent="save" novalidate>
        <!-- Event Organizer Name -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.organizerName') }} <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.name }"
            v-model="store.name"
            maxlength="255"
            required
          />
          <div v-if="errors.name" class="invalid-feedback">
            {{ errors.name[0] }}
          </div>
        </div>

        <!-- Phone (Primary) -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.phoneMain') }}</label>
          <input
            type="tel"
            class="form-control"
            :class="{ 'is-invalid': errors.phone_primary }"
            v-model="store.phonePrimary"
            maxlength="20"
            placeholder="0166-88-8888"
          />
          <div v-if="errors.phone_primary" class="invalid-feedback">
            {{ errors.phone_primary[0] }}
          </div>
        </div>

        <!-- Phone (Secondary) -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.phoneSub') }}</label>
          <input
            type="tel"
            class="form-control"
            :class="{ 'is-invalid': errors.phone_secondary }"
            v-model="store.phoneSecondary"
            maxlength="20"
            placeholder="0166-12-3456"
          />
          <div v-if="errors.phone_secondary" class="invalid-feedback">
            {{ errors.phone_secondary[0] }}
          </div>
        </div>

        <!-- Email (Primary) -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.emailMain') }}</label>
          <input
            type="email"
            class="form-control"
            :class="{ 'is-invalid': errors.email_primary }"
            v-model="store.emailPrimary"
            maxlength="255"
            placeholder="abcd@efghi.com"
          />
          <div v-if="errors.email_primary" class="invalid-feedback">
            {{ errors.email_primary[0] }}
          </div>
        </div>

        <!-- Phone (Secondary) -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.emailSub') }}</label>
          <input
            type="email"
            class="form-control"
            :class="{ 'is-invalid': errors.email_secondary }"
            v-model="store.emailSecondary"
            maxlength="255"
            placeholder="abcd@efghi.com"
          />
          <div v-if="errors.email_secondary" class="invalid-feedback">
            {{ errors.email_secondary[0] }}
          </div>
        </div>

        <!-- Residence Address -->
        <h5 class="mt-4 mb-3">{{ $t('eventOrganizers.address') }}</h5>

        <!-- Postal Code -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.postalCode') }} <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.postal_code }"
            v-model="store.postalCode"
            maxlength="10"
            :placeholder="$t('eventOrganizers.postalCodePlaceholder')"
            required
          />
          <div v-if="errors.postal_code" class="invalid-feedback">
            {{ errors.postal_code[0] }}
          </div>
        </div>

        <!-- Prefecture -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.prefecture') }} <span class="text-danger">*</span></label>
          <select
            class="form-select"
            :class="{ 'is-invalid': errors.prefecture }"
            v-model="store.prefecture"
            required
          >
            <option value="">{{ $t('eventOrganizers.prefectureDefault') }}</option>
            <option v-for="pref in PREFECTURES" :key="pref.value" :value="pref.value">
              {{ pref[$i18n.locale] || pref.ja }}
            </option>
          </select>
          <div v-if="errors.prefecture" class="invalid-feedback">
            {{ errors.prefecture[0] }}
          </div>
        </div>

        <!-- City -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.city') }} <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.city }"
            v-model="store.city"
            maxlength="100"
            :placeholder="$t('eventOrganizers.cityPlaceholder')"
            required
          />
          <div v-if="errors.city" class="invalid-feedback">
            {{ errors.city[0] }}
          </div>
        </div>

        <!-- District and so on -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.cityDetail') }}</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.town }"
            v-model="store.town"
            maxlength="100"
            :placeholder="$t('eventOrganizers.cityDetailPlaceholder')"
          />
          <div v-if="errors.town" class="invalid-feedback">
            {{ errors.town[0] }}
          </div>
        </div>

        <!-- Buildings -->
        <div class="mb-3">
          <label class="form-label">{{ $t('eventOrganizers.building') }}</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.building }"
            v-model="store.building"
            maxlength="200"
            :placeholder="$t('eventOrganizers.buildingPlaceholder')"
          />
          <div v-if="errors.building" class="invalid-feedback">
            {{ errors.building[0] }}
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons mt-4">
          <button type="submit" class="btn btn-primary btn-submit" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            {{ isEdit ? $t('eventOrganizers.update') : $t('eventOrganizers.create') }}
          </button>

          <button type="button" class="btn btn-cancel" @click="cancel">
            {{ $t('eventOrganizers.cancel') }}
          </button>

          <button
            v-if="isEdit"
            type="button"
            class="btn btn-link btn-delete"
            @click="confirmDelete"
          >
            <i class="bi bi-x-circle"></i> {{ $t('eventOrganizers.delete') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Error Dialog -->
    <ErrorDialog
      v-model="showErrorDialog"
      :message="errorDialogMessage"
    />

    <!-- Confirm Dialog -->
    <ConfirmDialog
      v-model="showConfirmDialog"
      :message="t('eventOrganizers.confirmDelete')"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { PREFECTURES } from '../../../../../consts/prefectures';
import { colors } from '../../../../../consts/colors';
import Store from '../../../../models/Store';
import {
  getEventOrganizer,
  createEventOrganizer,
  updateEventOrganizer,
  checkEventOrganizerDeletable,
  deleteEventOrganizer
} from '../../../../api/user/EventOrganizerApi';
import useFlashMessage from '../../../../composables/useFlashMessage';
import ErrorDialog from '../../../../components/common/ErrorDialog.vue';
import ConfirmDialog from '../../../../components/common/ConfirmDialog.vue';
import { isValidEmail, isValidPhoneNumber, isValidPostalCode } from '../../../../utils/validators';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { successMessage, errorMessage } = useFlashMessage();

const store = ref(new Store());
const errors = ref({});
const saving = ref(false);
const showErrorDialog = ref(false);
const errorDialogMessage = ref('');
const showConfirmDialog = ref(false);

const isEdit = computed(() => !!route.params.id);

/**
 * Client-side form validation
 */
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  // Organizer name validation
  if (!store.value.name || store.value.name.trim() === '') {
    errors.value.name = [t('eventOrganizers.validation.nameRequired')];
    isValid = false;
  } else if (store.value.name.length > 255) {
    errors.value.name = [t('eventOrganizers.validation.nameMaxLength')];
    isValid = false;
  }

  // Phone primary validation (optional but must be valid if provided)
  if (store.value.phonePrimary) {
    if (!isValidPhoneNumber(store.value.phonePrimary)) {
      errors.value.phone_primary = [t('eventOrganizers.validation.phoneInvalid')];
      isValid = false;
    } else if (store.value.phonePrimary.length > 20) {
      errors.value.phone_primary = [t('eventOrganizers.validation.phoneMaxLength')];
      isValid = false;
    }
  }

  // Phone secondary validation (optional but must be valid if provided)
  if (store.value.phoneSecondary) {
    if (!isValidPhoneNumber(store.value.phoneSecondary)) {
      errors.value.phone_secondary = [t('eventOrganizers.validation.phoneInvalid')];
      isValid = false;
    } else if (store.value.phoneSecondary.length > 20) {
      errors.value.phone_secondary = [t('eventOrganizers.validation.phoneMaxLength')];
      isValid = false;
    }
  }

  // Email primary validation (optional but must be valid if provided)
  if (store.value.emailPrimary) {
    if (!isValidEmail(store.value.emailPrimary)) {
      errors.value.email_primary = [t('eventOrganizers.validation.emailInvalid')];
      isValid = false;
    } else if (store.value.emailPrimary.length > 255) {
      errors.value.email_primary = [t('eventOrganizers.validation.emailMaxLength')];
      isValid = false;
    }
  }

  // Email secondary validation (optional but must be valid if provided)
  if (store.value.emailSecondary) {
    if (!isValidEmail(store.value.emailSecondary)) {
      errors.value.email_secondary = [t('eventOrganizers.validation.emailInvalid')];
      isValid = false;
    } else if (store.value.emailSecondary.length > 255) {
      errors.value.email_secondary = [t('eventOrganizers.validation.emailMaxLength')];
      isValid = false;
    }
  }

  // Postal code validation
  if (!store.value.postalCode || store.value.postalCode.trim() === '') {
    errors.value.postal_code = [t('eventOrganizers.validation.postalCodeRequired')];
    isValid = false;
  } else if (!isValidPostalCode(store.value.postalCode)) {
    errors.value.postal_code = [t('eventOrganizers.validation.postalCodeInvalid')];
    isValid = false;
  } else if (store.value.postalCode.length > 10) {
    errors.value.postal_code = [t('eventOrganizers.validation.postalCodeMaxLength')];
    isValid = false;
  }

  // Prefecture validation
  if (!store.value.prefecture || store.value.prefecture.trim() === '') {
    errors.value.prefecture = [t('eventOrganizers.validation.prefectureRequired')];
    isValid = false;
  }

  // City validation
  if (!store.value.city || store.value.city.trim() === '') {
    errors.value.city = [t('eventOrganizers.validation.cityRequired')];
    isValid = false;
  } else if (store.value.city.length > 100) {
    errors.value.city = [t('eventOrganizers.validation.cityMaxLength')];
    isValid = false;
  }

  // Town validation (optional but must be valid if provided)
  if (store.value.town && store.value.town.length > 100) {
    errors.value.town = [t('eventOrganizers.validation.townMaxLength')];
    isValid = false;
  }

  // Building validation (optional but must be valid if provided)
  if (store.value.building && store.value.building.length > 200) {
    errors.value.building = [t('eventOrganizers.validation.buildingMaxLength')];
    isValid = false;
  }

  return isValid;
};

onMounted(async () => {
  if (isEdit.value) {
    try {
      store.value = await getEventOrganizer(route.params.id);
    } catch (error) {
      errorMessage(error.message || t('eventOrganizers.errorFetchData'));
      router.push({ name: 'master.event_organizers.index' });
    }
  }
});

const save = async () => {
  // Client-side validation
  if (!validateForm()) {
    window.scrollTo(0, 0);
    return;
  }

  saving.value = true;
  errors.value = {};

  try {
    if (isEdit.value) {
      await updateEventOrganizer(store.value);
      successMessage(t('eventOrganizers.successUpdate'));
    } else {
      await createEventOrganizer(store.value);
      successMessage(t('eventOrganizers.successCreate'));
    }
    router.push({ name: 'master.event_organizers.index' });
  } catch (error) {
    if (error.status === 422 && error.errors) {
      errors.value = error.errors;
    } else {
      errorMessage(error.message || t('eventOrganizers.errorSave'));
    }
  } finally {
    saving.value = false;
  }
};

const cancel = () => {
  router.push({ name: 'master.event_organizers.index' });
};

const confirmDelete = async () => {
  try {
    // First, check if the organizer can be deleted
    const result = await checkEventOrganizerDeletable(store.value.id);

    if (!result.deletable) {
      // Show error dialog if not deletable
      errorDialogMessage.value = result.message || t('eventOrganizers.errorDeleteInUse');
      showErrorDialog.value = true;
    } else {
      // Show confirmation dialog if deletable
      showConfirmDialog.value = true;
    }
  } catch (error) {
    errorMessage(error.message || t('eventOrganizers.errorDelete'));
  }
};

const handleDeleteConfirm = async () => {
  try {
    // Execute deletion
    await deleteEventOrganizer(store.value.id);
    successMessage(t('eventOrganizers.successDelete'));
    router.push({ name: 'master.event_organizers.index' });
  } catch (error) {
    errorMessage(error.message || t('eventOrganizers.errorDelete'));
  }
};
</script>

<style scoped>
.container-fluid {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  width: 100%;
  max-width: 640px;
  margin-bottom: 1rem;
}

.page-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
  margin-bottom: 0.5rem;
}

/* Subtitle is not consistent with Import Page, therefore I refactored UI design on this view to be align with `resources/js/views/user/import/LedgHome.vue` */
/* .page-header .subtitle {
  font-size: 14px;
  color: v-bind('colors.gray[500]');
  margin-bottom: 0;
} */

.box {
  background-color: v-bind('colors.white');
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px v-bind('colors.shadowColor');
  max-width: 640px;
  width: 100%;
  margin: 0 auto;
}

h5 {
  font-weight: 600;
  color: v-bind('colors.gray[700]');
  border-bottom: 1px solid v-bind('colors.gray[200]');
  padding-bottom: 0.5rem;
}

.text-danger {
  font-weight: bold;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 2rem;
}

.btn-submit {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 500;
  background-color: v-bind('colors.btnBgColorPrimary');
  border: none;
  border-radius: 6px;
}

.btn-submit:hover:not(:disabled) {
  background-color: v-bind('colors.chartBlue[800]');
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 500;
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.disabled');
  border-radius: 6px;
}

.btn-cancel:hover {
  background-color: v-bind('colors.gray[50]');
  color: v-bind('colors.btnBgColorPrimary');
}

.btn-delete {
  width: 100%;
  padding: 12px;
  font-size: 14px;
  color: v-bind('colors.btnBgColorPrimary');
  text-decoration: none;
  border: none;
  background: none;
  text-align: center;
}

.btn-delete:hover {
  text-decoration: underline;
  color: v-bind('colors.chartBlue[800]');
}

.btn-delete i {
  margin-right: 4px;
}
</style>
